import React, { useState, useRef, useEffect } from "react";
import { BsShieldLock } from "react-icons/bs";
import { useLocation, useNavigate } from "react-router-dom";
import { useDarkMode } from "../hooks/useDarkMode";
import {
  requestLocationPermission,
  isLocationValid,
} from "../utils/locationService";

export default function Login2FA() {
  const { isDarkMode } = useDarkMode();
  const location = useLocation();
  const navigate = useNavigate();

  const stateEmail =
    location?.state?.email || localStorage.getItem("pending2FA");
  const mode = location?.state?.mode || "login"; // login | enable-2fa

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const inputRefs = useRef([]);

  /* ======================
     EFFECTS
  ======================= */

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (!stateEmail) {
      setError("Session expired. Please login again.");
      setTimeout(() => navigate("/signin"), 2000);
    }
  }, [stateEmail, navigate]);

  /* ======================
     HELPERS
  ======================= */

  const maskEmail = (email) => {
    if (!email) return "";
    const [name, domain] = email.split("@");
    return name.slice(0, 3) + "****@" + domain;
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter") handleVerify();
  };

  /* ======================
     API ACTIONS
  ======================= */

  const completeLogin = () => {
    localStorage.removeItem("pending2FA");

    if (isLocationValid()) {
      navigate("/dashboard");
    } else {
      requestLocationPermission(
        () => navigate("/dashboard"),
        () => navigate("/dashboard")
      );
    }
  };

  const handleVerify = async () => {
    setError("");
    setSuccess("");

    const code = otp.join("");
    if (code.length !== 6) {
      setError("Please enter the 6-digit code");
      return;
    }

    try {
      setLoading(true);

      if (mode === "enable-2fa") {
        const { verify2FAEnableCodeApi } = await import(
          "../apis/payments.api"
        );
        await verify2FAEnableCodeApi(code);

        setSuccess("2FA enabled successfully!");

        setTimeout(() => {
          navigate("/dashboard/settings", {
            state: { success: "2FA enabled successfully" },
            replace: true,
          });
        }, 1500);
      } else {
        const { verify2FALoginCodeApi } = await import(
          "../apis/payments.api"
        );
        await verify2FALoginCodeApi(code);

        setSuccess("Verification successful!");
        setTimeout(completeLogin, 1000);
      }
    } catch (err) {
      setError(err?.message || "Invalid or expired code");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setSuccess("");

    try {
      setResending(true);

      if (mode === "enable-2fa") {
        const { send2FAEnableCodeApi } = await import(
          "../apis/payments.api"
        );
        await send2FAEnableCodeApi();
      } else {
        const { resend2FACodeApi } = await import("../apis/payments.api");
        await resend2FACodeApi();
      }

      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
      setSuccess("A new code has been sent");
    } catch {
      setError("Failed to resend code");
    } finally {
      setResending(false);
    }
  };

  /* ======================
     UI
  ======================= */

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
    >
      {/* ===== BACKGROUND CIRCLES ===== */}
      <div className="absolute -top-24 -left-24 w-72 h-72 bg-[#008300]/10 rounded-full"></div>
      <div className="absolute top-40 right-0 w-64 h-64 bg-[#0B1B3F]/10 rounded-full"></div>
      <div
        className={`w-full max-w-md rounded-2xl shadow-lg p-8 ${isDarkMode ? "bg-gray-800 text-white" : "bg-white"
          }`}
      >
        <div className="flex flex-col items-center gap-4">
          <BsShieldLock className="text-5xl text-[#008300]" />

          <h2 className="text-2xl font-bold">Two-Factor Authentication</h2>

          <p className="text-center text-sm opacity-80">
            Enter the verification code sent to <br />
            <strong>{maskEmail(stateEmail)}</strong>
          </p>

          {/* OTP INPUTS */}
          <div className="flex gap-3 mt-4">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                value={digit}
                maxLength={1}
                inputMode="numeric"
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-12 text-center text-xl font-bold rounded-lg bg-[#008300] text-white focus:outline-none focus:ring-2 focus:ring-green-400"
              />
            ))}
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}
          {success && <p className="text-green-500 text-sm">{success}</p>}

          <button
            onClick={handleVerify}
            disabled={loading}
            className="w-full mt-4 py-2 rounded-full bg-[#008300] hover:bg-[#006600] text-white font-semibold transition"
          >
            {loading ? "Verifying..." : "Verify"}
          </button>

          <p className="text-sm mt-2">
            Didn’t receive the code?{" "}
            <button
              onClick={handleResend}
              disabled={resending}
              className="text-[#008300] font-semibold hover:underline"
            >
              {resending ? "Resending..." : "Resend"}
            </button>
          </p>

          <button
            onClick={() =>
              navigate(mode === "enable-2fa" ? "/dashboard/settings" : "/signin")
            }
            className="text-sm mt-2 opacity-70 hover:opacity-100"
          >
            {mode === "enable-2fa" ? "Back to Settings" : "Back to Login"}
          </button>
        </div>
      </div>
    </div>
  );
}
